import React, { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { X, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { taskService } from '@/services/taskService'

// Same normalisation as the server (labelService): "Login Page" → "login-page"
export const normalizeLabel = (value) => String(value || '')
  .trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9._:/+#-]/g, '').replace(/-{2,}/g, '-').slice(0, 50)

// Deterministic soft color per label so the same label always looks the same
const LABEL_COLORS = [
  'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200',
  'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-200',
]
export const labelColor = (label) => {
  let hash = 0
  for (const ch of label) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  return LABEL_COLORS[hash % LABEL_COLORS.length]
}

export const LabelChip = ({ label, onRemove, className }) => (
  <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-medium', labelColor(label), className)}>
    {label}
    {onRemove && (
      <button type="button" onClick={onRemove} className="hover:opacity-70" title={`Remove ${label}`}>
        <X className="h-3 w-3" />
      </button>
    )}
  </span>
)

// Chips + input with suggestions from labels already used in the project.
const LabelsInput = ({ value = [], onChange, projectId, placeholder = 'Add label…', className }) => {
  const [text, setText] = useState('')
  const [focused, setFocused] = useState(false)
  const { data } = useQuery(
    ['projects', String(projectId), 'labels'],
    () => taskService.getProjectLabels(projectId),
    { enabled: !!projectId, staleTime: 30000 }
  )

  const suggestions = useMemo(() => {
    const typed = normalizeLabel(text)
    return (data?.data?.labels || [])
      .map(l => l.label)
      .filter(l => !value.includes(l) && (!typed || l.includes(typed)))
      .slice(0, 8)
  }, [data, text, value])

  const add = (raw) => {
    const label = normalizeLabel(raw)
    setText('')
    if (!label || value.includes(label)) return
    onChange([...value, label])
  }

  const remove = (label) => onChange(value.filter(l => l !== label))

  return (
    <div className="relative">
      <div className={cn('flex flex-wrap items-center gap-1 rounded-md border px-2 py-1 min-h-8', focused && 'ring-1 ring-ring', className)}>
        <Tag className="h-3.5 w-3.5 text-muted-foreground" />
        {value.map(label => <LabelChip key={label} label={label} onRemove={() => remove(label)} />)}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ',') && text.trim()) { e.preventDefault(); add(text) }
            if (e.key === 'Backspace' && !text && value.length) remove(value[value.length - 1])
          }}
          placeholder={value.length ? '' : placeholder}
          className="flex-1 min-w-[80px] bg-transparent text-sm outline-none py-0.5"
        />
      </div>
      {focused && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover p-1 shadow-md">
          {suggestions.map(label => (
            <button
              key={label}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); add(label) }}
              className="w-full text-left px-2 py-1 rounded-sm hover:bg-accent"
            >
              <LabelChip label={label} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LabelsInput
